import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/common/Button';
import { BookOpen } from 'lucide-react';
import { Badge, StatusBadge } from '@/components/common/Badge';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { TextField, SelectField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { useResourceActions } from '@/hooks/useResourceActions';
import { subjectsApi } from '@/services/api/endpoints/academic';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { AcademicStatus, Subject, SubjectType } from '@/types/domain';
import { humanise, orDash } from '@/utils/format';

const SUBJECT_TYPES: { value: SubjectType; label: string }[] = [
  { value: 'CORE', label: 'Core' },
  { value: 'ELECTIVE', label: 'Elective' },
  { value: 'PRACTICAL', label: 'Practical' },
  { value: 'CO_CURRICULAR', label: 'Co-curricular' },
  { value: 'OTHER', label: 'Other' },
];

const schema = z.object({
  code: z.string().min(1, 'Code is required').max(32),
  name: z.string().min(1, 'Name is required').max(100),
  shortName: z.string().max(32),
  description: z.string().max(500),
  subjectType: z.enum(['CORE', 'ELECTIVE', 'PRACTICAL', 'CO_CURRICULAR', 'OTHER']),
});

type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues = { code: '', name: '', shortName: '', description: '', subjectType: 'CORE' };

/**
 * The school-wide subject catalogue.
 *
 * Which grades offer a subject is managed from the grade, not here — that is the
 * direction schools work in, building up a grade's curriculum.
 */
export function SubjectsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const table = useTableQuery('name,asc');

  const [typeFilter, setTypeFilter] = useState<SubjectType | ''>('');
  const [statusFilter, setStatusFilter] = useState<AcademicStatus | ''>('');
  const [editing, setEditing] = useState<Subject | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<Subject | null>(null);

  const params = {
    ...table.params,
    ...(typeFilter !== '' ? { subjectType: typeFilter } : {}),
    ...(statusFilter !== '' ? { status: statusFilter } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.subjects.list(params),
    queryFn: () => subjectsApi.list(params),
  });

  const { statusMutation, deleteMutation, invalidateAll } = useResourceActions({
    invalidates: [queryKeys.subjects.all, queryKeys.grades.all],
    changeStatus: subjectsApi.changeStatus,
    remove: subjectsApi.remove,
    label: 'Subject',
  });

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY });

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const blank = (value: string) => (value === '' ? undefined : value);
      const payload = {
        ...values,
        shortName: blank(values.shortName),
        description: blank(values.description),
      };
      return editing === null ? subjectsApi.create(payload) : subjectsApi.update(editing.id, payload);
    },
    onSuccess: () => {
      toast.success(editing === null ? 'Subject created' : 'Subject updated');
      close();
      // A rename shows up in every grade that offers this subject.
      invalidateAll();
    },
    onError: (error) => handleApiError(error, form.setError),
  });

  function openCreate() {
    setEditing(null);
    form.reset(EMPTY);
    setOpen(true);
  }

  function openEdit(subject: Subject) {
    setEditing(subject);
    form.reset({
      code: subject.code,
      name: subject.name,
      shortName: subject.shortName ?? '',
      description: subject.description ?? '',
      subjectType: subject.subjectType,
    });
    setOpen(true);
  }

  function close() {
    setOpen(false);
    setEditing(null);
  }

  const columns: Column<Subject>[] = [
    {
      key: 'code',
      header: 'Code',
      sortKey: 'code',
      render: (subject) => <span className="font-medium text-ink">{subject.code}</span>,
    },
    { key: 'name', header: 'Name', sortKey: 'name', render: (subject) => subject.name },
    {
      key: 'shortName',
      header: 'Short name',
      sortKey: 'shortName',
      render: (subject) => orDash(subject.shortName),
    },
    {
      key: 'type',
      header: 'Type',
      sortKey: 'subjectType',
      render: (subject) => <Badge tone="neutral">{humanise(subject.subjectType)}</Badge>,
    },
    {
      key: 'grades',
      header: 'Grades',
      render: (subject) => <span className="tabular-nums">{subject.gradeCount}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (subject) => <StatusBadge status={subject.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (subject) => (
        <div className="flex justify-end gap-2">
          {can(PERMISSIONS.SUBJECT_UPDATE) && (
            <>
              <Button size="sm" variant="ghost" onClick={() => openEdit(subject)}>
                Edit
              </Button>
              <Button
                size="sm"
                variant="ghost"
                loading={statusMutation.isPending}
                onClick={() =>
                  statusMutation.mutate({
                    id: subject.id,
                    payload: { status: subject.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' },
                  })
                }
              >
                {subject.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
              </Button>
            </>
          )}
          {can(PERMISSIONS.SUBJECT_DELETE) && (
            <Button size="sm" variant="ghost" onClick={() => setDeleting(subject)}>
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <ListPage
        title="Subjects"
        description="The school's subject catalogue. Assign subjects to grades from the grade itself."
        icon={<BookOpen className="h-5 w-5" aria-hidden="true" />}
        action={can(PERMISSIONS.SUBJECT_CREATE) ? <Button onClick={openCreate}>New subject</Button> : undefined}
        search={{ value: table.searchInput, onChange: table.setSearchInput, placeholder: 'Search subjects…' }}
        filters={
          <>
            <FilterSelect
              label="Type"
              value={typeFilter}
              onChange={(value) => {
                setTypeFilter(value);
                table.setPage(0);
              }}
              options={SUBJECT_TYPES}
            />
            <FilterSelect
              label="Status"
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                table.setPage(0);
              }}
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'INACTIVE', label: 'Inactive' },
              ]}
            />
          </>
        }
        pagination={query.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={query.data?.data}
          rowKey={(subject) => subject.id}
          loading={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="No subjects yet"
          emptyDescription="Add subjects once, then offer them to the grades that teach them."
        />
      </ListPage>

      <Modal
        open={open}
        title={editing === null ? 'New subject' : `Edit ${editing.name}`}
        onClose={close}
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button onClick={form.handleSubmit((values) => save.mutate(values))} loading={save.isPending}>
              Save
            </Button>
          </>
        }
      >
        <form className="space-y-4" noValidate onSubmit={form.handleSubmit((values) => save.mutate(values))}>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Code"
              required
              hint="Unique within the school"
              error={form.formState.errors.code?.message}
              {...form.register('code')}
            />
            <TextField
              label="Name"
              required
              error={form.formState.errors.name?.message}
              {...form.register('name')}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Short name"
              hint="For timetables and other dense views"
              error={form.formState.errors.shortName?.message}
              {...form.register('shortName')}
            />
            <SelectField label="Type" {...form.register('subjectType')}>
              {SUBJECT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </SelectField>
          </div>
          <TextField
            label="Description"
            error={form.formState.errors.description?.message}
            {...form.register('description')}
          />
        </form>
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title="Delete subject"
        message={
          deleting === null
            ? ''
            : `Delete ${deleting.name}? This is refused while any grade still offers it.`
        }
        confirmLabel="Delete"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleting !== null) {
            deleteMutation.mutate(deleting.id, { onSettled: () => setDeleting(null) });
          }
        }}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}
